import React, { useState, useEffect } from "react";
import "./PRDEditor.css";

const PRDEditor = ({ sessionId, prdContent, onPrdUpdate }) => {
  const [localPrdContent, setLocalPrdContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

  // Fetch PRD content from backend when sessionId changes
  useEffect(() => {
    const fetchPRD = async () => {
      if (!sessionId) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${backendUrl}/sessions/${sessionId}/prd`);

        if (!response.ok) {
          if (response.status === 404) {
            // PRD doesn't exist yet, that's okay
            setLocalPrdContent("");
            return;
          }
          throw new Error(`Failed to fetch PRD: ${response.status}`);
        }

        const result = await response.json();
        if (result.prd !== undefined) {
          setLocalPrdContent(result.prd);
          if (onPrdUpdate) {
            onPrdUpdate(result.prd);
          }
        }
      } catch (err) {
        console.error("Error fetching PRD:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPRD();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, backendUrl]);

  // Update local content when prdContent prop changes (fallback for parent updates)
  useEffect(() => {
    if (prdContent !== undefined && prdContent !== localPrdContent) {
      setLocalPrdContent(prdContent);
    }
  }, [prdContent, localPrdContent]);

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setLocalPrdContent(newContent);
    // Notify parent component of the change
    if (onPrdUpdate) {
      onPrdUpdate(newContent);
    }
  };

  const savePRD = async () => {
    if (!sessionId || !localPrdContent.trim()) return;

    setIsSaving(true);
    setError(null);

    try {
      // Use the direct Product Requirements Document API endpoint to update the content
      const response = await fetch(`${backendUrl}/sessions/${sessionId}/prd`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prd: localPrdContent,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to save Product Requirements Document: ${response.status}`
        );
      }

      const result = await response.json();

      if (result.prd && onPrdUpdate) {
        onPrdUpdate(result.prd);
      }

      setLastSaved(new Date());
    } catch (err) {
      console.error("Error saving Product Requirements Document:", err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const downloadPRD = async (format) => {
    if (!sessionId) return;

    try {
      const endpoint =
        format === "pdf"
          ? `${backendUrl}/sessions/${sessionId}/prd/pdf`
          : `${backendUrl}/sessions/${sessionId}/prd/download`;

      const response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error(
          `Failed to download Product Requirements Document: ${response.status}`
        );
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `PRD-${sessionId}.${format === "pdf" ? "pdf" : "md"}`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(
        `Error downloading Product Requirements Document as ${format}:`,
        err
      );
      setError(
        `Failed to download Product Requirements Document as ${format.toUpperCase()}`
      );
    }
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      savePRD();
    }
  };

  if (!sessionId) {
    return (
      <div className="prd-editor">
        <div className="prd-editor-placeholder">
          <p>
            Please start a chat session to edit the Product Requirements
            Document
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="prd-editor">
        <div className="prd-editor-placeholder">
          <span className="spinner"></span>
          <p>Loading Product Requirements Document...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="prd-editor">
      <div className="prd-editor-header">
        <h3>Product Requirements Document</h3>
        <div className="prd-editor-actions">
          {lastSaved && (
            <span className="last-saved">
              Saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
          <div className="action-buttons">
            <button
              className="save-button"
              onClick={savePRD}
              disabled={isSaving || isLoading}
            >
              {isSaving ? (
                <>
                  <span className="spinner small"></span>
                  Saving...
                </>
              ) : (
                <>💾 Save</>
              )}
            </button>
            <button
              className="download-button"
              onClick={() => downloadPRD("markdown")}
              title="Download as Markdown"
              disabled={isLoading}
            >
              📄 MD
            </button>
            <button
              className="download-button"
              onClick={() => downloadPRD("pdf")}
              title="Download as PDF"
              disabled={isLoading}
            >
              📋 PDF
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <div className="prd-editor-content">
        <textarea
          className="prd-textarea"
          value={localPrdContent}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter your Product Requirements Document here..."
          spellCheck="true"
        />
      </div>

      <div className="prd-editor-footer">
        <div className="keyboard-shortcuts">
          <span>💡 Tip: Press Ctrl+S (Cmd+S on Mac) to save quickly</span>
        </div>
      </div>
    </div>
  );
};

export default PRDEditor;
